export type TransactionType = 'COTIZACION' | 'VENTA';

export type QuotationStatus = 'CREADA' | 'ENVIADA' | 'ACEPTADA' | 'RECHAZADA' | 'VENCIDA' | 'FINALIZADA';
export type SaleStatus = 'PENDIENTE' | 'PAGADA' | 'REEMBOLSADA' | 'ANULADA';

export interface TransactionItem {
  id?: number;
  transaccion_id?: number;
  item_id: number;
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
  descuento: number;
  tasa_impuesto: number;
  total_linea: number;
  // For display purposes
  item?: {
    id: number;
    name: string;
    type: 'PRODUCTO' | 'SERVICIO';
    price: number;
    tax_rate: number;
  };
}

export interface TransactionPayment {
  id?: number;
  transaccion_id?: number;
  metodo: 'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA' | 'CHEQUE' | 'OTRO';
  monto: number;
  autorizacion?: string;
  notas?: string;
  created_at?: string;
}

export interface Transaction {
  id: number;
  company_id: number;
  numero: string;
  tipo: TransactionType;
  estado: QuotationStatus | SaleStatus;
  cliente_id: number;
  vendedor_id: number;
  condiciones_pago?: string;
  validez?: string;
  subtotal: number;
  impuestos: number;
  descuentos: number;
  total: number;
  saldo: number;
  moneda: string;
  nota?: string;
  origen_cotizacion_id?: number;
  created_at: string;
  updated_at: string;
  // Relations
  customer?: {
    id: number;
    name: string;
    doc_number: string;
    email?: string;
    phone?: string;
  };
  cliente?: {
    id: number;
    name: string;
    doc_number: string;
    email?: string;
    phone?: string;
  };
  seller?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  vendedor?: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  items?: TransactionItem[];
  payments?: TransactionPayment[];
}

export interface TransactionItemCreate {
  item_id: number;
  descripcion: string;
  cantidad: number;
  precio_unitario: number;
  descuento: number;
  tasa_impuesto: number;
}

export interface TransactionCreateData {
  tipo: TransactionType;
  cliente_id: number;
  vendedor_id?: number;
  condiciones_pago?: string;
  validez?: string;
  descuentos?: number;
  moneda?: string;
  nota?: string;
  origen_cotizacion_id?: number;
  items: TransactionItemCreate[];
}

export interface TransactionUpdateData extends Partial<TransactionCreateData> {
  estado?: QuotationStatus | SaleStatus;
}

export interface TransactionFilters {
  search?: string;
  tipo?: TransactionType | '';
  estado?: string;
  cliente_id?: number | '';
  vendedor_id?: number | '';
  date_from?: string;
  date_to?: string;
}

export interface TransactionPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface TransactionsResponse {
  transactions: Transaction[];
  pagination: TransactionPagination;
}

export interface TransactionStats {
  total: number;
  cotizaciones: number;
  ventas: number;
  cotizaciones_enviadas: number;
  cotizaciones_aceptadas: number;
  ventas_pendientes: number;
  ventas_pagadas: number;
  total_amount: number;
  pending_amount: number;
}

// Constants
export const TRANSACTION_TYPES: { value: TransactionType; label: string }[] = [
  { value: 'COTIZACION', label: 'Cotización' },
  { value: 'VENTA', label: 'Venta' }
];

export const QUOTATION_STATUSES: { value: QuotationStatus; label: string }[] = [
  { value: 'CREADA', label: 'Creada' },
  { value: 'ENVIADA', label: 'Enviada' },
  { value: 'ACEPTADA', label: 'Aceptada' },
  { value: 'RECHAZADA', label: 'Rechazada' },
  { value: 'VENCIDA', label: 'Vencida' },
  { value: 'FINALIZADA', label: 'Finalizada' }
];

export const SALE_STATUSES: { value: SaleStatus; label: string }[] = [
  { value: 'PENDIENTE', label: 'Pendiente' },
  { value: 'PAGADA', label: 'Pagada' },
  { value: 'REEMBOLSADA', label: 'Reembolsada' },
  { value: 'ANULADA', label: 'Anulada' }
];

export const PAYMENT_METHODS: { value: string; label: string }[] = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TARJETA', label: 'Tarjeta' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'OTRO', label: 'Otro' }
];

export const CURRENCY_OPTIONS: { value: string; label: string }[] = [
  { value: 'GTQ', label: 'GTQ (Quetzal)' },
  { value: 'USD', label: 'USD (Dólar)' }
];


// Form data interfaces
export interface TransactionFormData {
  cliente_id: number;
  condiciones_pago: string;
  validez?: Date;
  descuentos: number;
  moneda: 'GTQ' | 'USD';
  nota: string;
  items?: TransactionItemFormData[];
}

export interface TransactionItemFormData {
  item_id: number;
  cantidad: number;
  precio_unitario: number;
  descuento: number;
}

export interface PaymentFormData {
  metodo: 'EFECTIVO' | 'TARJETA' | 'TRANSFERENCIA' | 'OTRO';
  monto: number;
  autorizacion: string;
  notas: string;
}